/**
 * Proportional focus boxes: a frame's natural size placed in the box of its
 * container. A tool (`normal`) is fitted into the comfortable normal box at
 * any scale. A media, reading or object size is the card's shape at the
 * opened size (`lib/focus/focus-opened-size.ts`), and a `compact` one is a
 * small prompt's content (a connection prompt), so each is final: the
 * layout only scales it down to fit, never up. A compact prompt docks at
 * the right edge instead of the centre.
 */
import {
  calculateNormalFocusWindowBox,
  FOCUS_WINDOW_MARGIN,
  roundBox,
  type FocusWindowBox,
} from "@/lib/focus-window-layout";

/** The box a natural size is fitted into. */
type FocusContainer = "media" | "reading" | "object" | "compact" | "normal";

export interface FocusOriginalSize {
  width: number;
  height: number;
  /** `normal` when absent: a tool, or a canvas node of a reading type. */
  container?: FocusContainer;
}

/** The media box: the share of the surface an image, page or PDF may take. */
const MEDIA_W_RATIO = 0.86;
const MEDIA_H_RATIO = 0.82;
/** A reading column is at most this share of the surface's height. */
export const READING_H_RATIO = 0.82;

/**
 * A compact prompt (a Connect card) docks at the right edge and does not
 * block: no dimmed backdrop, the rest stays usable.
 */
export function isDockedSize(
  size: FocusOriginalSize | undefined | null,
): boolean {
  return size?.container === "compact";
}

export function isValidOriginalSize(
  size: FocusOriginalSize | undefined | null,
): size is FocusOriginalSize {
  return (
    !!size &&
    Number.isFinite(size.width) &&
    Number.isFinite(size.height) &&
    size.width > 0 &&
    size.height > 0
  );
}

/** `ratio` of the surface on each side, inside the margins, centred. */
function centredShareBox(
  surface: FocusWindowBox,
  widthRatio: number,
  heightRatio: number,
): FocusWindowBox {
  const width = Math.max(
    1,
    Math.min(
      surface.width * widthRatio,
      surface.width - FOCUS_WINDOW_MARGIN * 2,
    ),
  );
  const height = Math.max(
    1,
    Math.min(
      surface.height * heightRatio,
      surface.height - FOCUS_WINDOW_MARGIN * 2,
    ),
  );
  return roundBox({
    x: surface.x + (surface.width - width) / 2,
    y: surface.y + (surface.height - height) / 2,
    width,
    height,
  });
}

/** The media box: 86% × 82% of the surface, centred, inside the margins. */
export function calculateMediaFocusWindowBox(
  surface: FocusWindowBox,
): FocusWindowBox {
  return centredShareBox(surface, MEDIA_W_RATIO, MEDIA_H_RATIO);
}

function containerBox(
  surface: FocusWindowBox,
  container: FocusContainer | undefined,
): FocusWindowBox {
  switch (container) {
    case "media":
      return calculateMediaFocusWindowBox(surface);
    case "reading":
      return centredShareBox(surface, 1, READING_H_RATIO);
    case "object":
    case "compact":
      return centredShareBox(surface, 1, 1);
    default:
      return calculateNormalFocusWindowBox(surface);
  }
}

/**
 * Place `original` in `box`: centred, or docked at its right edge for a
 * compact prompt. A tool keeps its aspect at any scale. Media, objects and
 * compact prompts keep their aspect and only shrink. A reading column
 * scrolls, so each side is clamped on its own.
 */
function fitIntoBox(
  original: FocusOriginalSize,
  box: FocusWindowBox,
): FocusWindowBox {
  const container = original.container ?? "normal";
  let width: number;
  let height: number;
  if (container === "reading") {
    width = Math.min(original.width, box.width);
    height = Math.min(original.height, box.height);
  } else {
    const fit = Math.min(
      box.width / original.width,
      box.height / original.height,
    );
    const scale = container === "normal" ? fit : Math.min(fit, 1);
    if (!Number.isFinite(scale) || scale <= 0) return roundBox(box);
    width = original.width * scale;
    height = original.height * scale;
  }
  width = Math.max(1, Math.round(width));
  height = Math.max(1, Math.round(height));
  return roundBox({
    x:
      container === "compact"
        ? box.x + box.width - width
        : box.x + (box.width - width) / 2,
    y: box.y + (box.height - height) / 2,
    width,
    height,
  });
}

/** Single-frame focus: the natural size in its container's box. */
export function calculateProportionalNormalBox(
  surface: FocusWindowBox,
  original?: FocusOriginalSize | null,
): FocusWindowBox {
  if (!isValidOriginalSize(original)) {
    return calculateNormalFocusWindowBox(surface);
  }
  return fitIntoBox(original, containerBox(surface, original.container));
}
